import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import {
  AdminUser,
  createSubAdmin,
  deleteAdminUser,
  getCurrentAdmin,
  listAdminUsers,
  resendAdminVerification,
  updateAdminUser,
} from "../../utils/api";
import {
  ADMIN_STATUS,
  ADMIN_STATUS_JA_LABEL,
} from "../../utils/constants/admin";
import { isUnauthorized } from "../../utils/isUnauthorized";
import { trimInput, trimStart } from "../../utils/common";

const AdminAccountsPage: React.FC = () => {
  const [meRole, setMeRole] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [form] = Form.useForm();
  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const [me, data] = await Promise.all([
        getCurrentAdmin(),
        listAdminUsers(),
      ]);
      setMeRole(me.role);
      setUsers(data);
    } catch (e: any) {
      if (!isUnauthorized(e)) {
        message.error(
          e?.response?.data?.error || "アカウント一覧の取得に失敗しました",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id: number) => {
    try {
      await deleteAdminUser(id);
      message.success("削除しました");
      await load();
    } catch (e: any) {
      if (!isUnauthorized(e)) {
        message.error(e?.response?.data?.error || "削除に失敗しました");
      }
    }
  };

  const onOpenEdit = (user: AdminUser) => {
    setEditTarget(user);
    editForm.setFieldsValue({
      form_field_user_change: user.username,
      form_field_pass_change: "",
    });
    setEditOpen(true);
  };

  const onSubmitEdit = async () => {
    if (!editTarget) return;
    try {
      const vals = await editForm.validateFields();

      if (!vals.form_field_user_change && !vals.form_field_pass_change) {
        message.info("変更はありません");
        setEditOpen(false);
        return;
      }
      await updateAdminUser(editTarget.id, {
        username: vals.form_field_user_change,
        password: vals.form_field_pass_change,
      });
      if (vals.form_field_user_change !== editTarget.username) {
        message.success(
          "確認メールを送信しました。メールのリンクから確認してください。",
        );
      } else {
        message.success("更新しました");
      }
      setEditOpen(false);
      setEditTarget(null);
      await load();
    } catch (e: any) {
      if (e?.errorFields) return; // form validation
      message.error(e?.response?.data?.error || "更新に失敗しました");
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (openCreate) {
      form.resetFields();
    }
  }, [openCreate]);

  const onCreate = async () => {
    try {
      const vals = await form.validateFields();
      // Form collects 'username' as the email address. Backend expects 'email' field.
      await createSubAdmin({
        email: vals.form_field_user_sub,
        password: vals.form_field_pass_sub,
        username: vals.form_field_user_sub || undefined,
      });
      message.success("サブ管理者を作成しました");
      setOpenCreate(false);
      form.resetFields();
      await load();
    } catch (e: any) {
      if (e?.errorFields) return; // form errors
      message.error(e?.response?.data?.error || "作成に失敗しました");
    }
  };

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", key: "id", width: 80 },
      { title: "メール", dataIndex: "username", key: "username" },
      { title: "権限", dataIndex: "role", key: "role", width: 120 },
      {
        title: "状態",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (v: keyof typeof ADMIN_STATUS_JA_LABEL | undefined) => {
          const code = (v ??
            ADMIN_STATUS.UNVERIFIED) as keyof typeof ADMIN_STATUS_JA_LABEL;
          const label = ADMIN_STATUS_JA_LABEL[code];
          const color =
            code === ADMIN_STATUS.ACTIVE || code === ADMIN_STATUS.CHANGEPASSWORD
              ? "green"
              : "gold";
          return <Tag color={color}>{label}</Tag>;
        },
      },
      {
        title: "アクション",
        key: "action",
        width: 280,
        render: (_: any, record: AdminUser) => (
          <Space wrap>
            <Button size="small" onClick={() => onOpenEdit(record)}>
              編集
            </Button>
            {!["ACTIVE", "CHANGEPASSWORD"].includes(
              record.status ?? "UNVERIFIED",
            ) && (
              <Button
                size="small"
                onClick={async () => {
                  try {
                    await resendAdminVerification(record.id);
                    message.success("確認メールを再送しました");
                  } catch (e: any) {
                    message.error(
                      e?.response?.data?.error || "再送に失敗しました",
                    );
                  }
                }}
              >
                確認メール再送
              </Button>
            )}
            <Popconfirm
              title="このアカウントを削除しますか？"
              description={
                <div>
                  <div>
                    <b>ID:</b> {record.id}
                  </div>
                  <div>
                    <b>メール:</b> {record.username}
                  </div>
                </div>
              }
              onConfirm={() => onDelete(record.id)}
              okText="削除"
              cancelText="キャンセル"
            >
              <Button size="small" danger>
                削除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [],
  );

  if (meRole !== "SUPER") {
    return <div>権限がありません。</div>;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <Typography.Title level={3} style={{ margin: 0 }}>
          アカウント管理
        </Typography.Title>
        <Space>
          <Button onClick={load}>再読み込み</Button>
          <Button type="primary" onClick={() => setOpenCreate(true)}>
            新規サブ管理者
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={users}
        columns={columns}
        pagination={false}
      />

      <Modal
        title="サブ管理者を作成"
        open={openCreate}
        onCancel={() => setOpenCreate(false)}
        onOk={onCreate}
        okText="作成"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form form={form} layout="vertical" autoComplete="off">
          <input type="text" name="fake_username" style={{ display: "none" }} />
          <input
            type="password"
            name="fake_password"
            style={{ display: "none" }}
          />
          <Form.Item
            label="メール"
            name="form_field_user_sub"
            rules={[
              { required: true, message: "メールを入力してください" },
              {
                pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                message: "正しいメール形式で入力してください",
              },
            ]}
          >
            <Input
              maxLength={255}
              onChange={(e) => {
                const trimmed = e.target.value.trimStart();
                form.setFieldValue("form_field_user_sub", trimmed);
                form.validateFields(["form_field_user_sub"]);
              }}
              onBlur={(e) => {
                form.setFieldValue(
                  "form_field_user_sub",
                  trimInput(e.target.value),
                );
                form.validateFields(["form_field_user_sub"]);
              }}
              autoComplete="new-password"
              readOnly
              onFocus={(e) => e.target.removeAttribute("readonly")}
            />
          </Form.Item>
          <Form.Item
            label="パスワード"
            name="form_field_pass_sub"
            rules={[
              { required: true, message: "パスワードを入力してください" },
              {
                min: 6,
                max: 255,
                message: "6文字以上255文字以内で入力してください",
              },
            ]}
          >
            <Input.Password
              maxLength={255}
              autoComplete="new-password"
              onChange={(e) => {
                form.setFieldValue(
                  "form_field_pass_sub",
                  trimStart(e.target.value),
                );
                form.validateFields(["form_field_pass_sub"]);
              }}
              onBlur={(e) => {
                form.setFieldValue(
                  "form_field_pass_sub",
                  trimInput(e.target.value),
                );
                form.validateFields(["form_field_pass_sub"]);
              }}
            />
          </Form.Item>
          <Form.Item
            label="パスワードの再入力"
            name="passwordConfirm_sub"
            dependencies={["form_field_pass_sub"]}
            rules={[
              {
                required: true,
                message: "パスワード（確認）を入力してください",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (
                    !value ||
                    getFieldValue("form_field_pass_sub") === value
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("パスワードが一致しません"));
                },
              }),
            ]}
          >
            <Input.Password
              maxLength={255}
              onChange={(e) => {
                form.setFieldValue(
                  "passwordConfirm_sub",
                  trimStart(e.target.value),
                );
                form.validateFields(["passwordConfirm_sub"]);
              }}
              onBlur={(e) => {
                form.setFieldValue(
                  "passwordConfirm_sub",
                  trimInput(e.target.value),
                );
                form.validateFields(["passwordConfirm_sub"]);
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Admin Modal */}
      <Modal
        title="管理者を編集"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={onSubmitEdit}
        okText="保存"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" autoComplete="off">
          <Form.Item
            label="メール (変更すると確認が必要です)"
            name="form_field_user_change"
            rules={[
              { required: true, message: "メールを入力してください" },
              {
                pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                message: "正しいメール形式で入力してください",
              },
            ]}
          >
            <Input
              maxLength={255}
              autoComplete="new-password"
              readOnly
              onFocus={(e) => e.target.removeAttribute("readonly")}
            />
          </Form.Item>
          <Form.Item
            label="新しいパスワード (任意)"
            name="form_field_pass_change"
            rules={[
              {
                min: 6,
                max: 255,
                message: "6文字以上255文字以内で入力してください",
              },
            ]}
          >
            <Input.Password
              maxLength={255}
              autoComplete="new-password"
              onChange={(e) => {
                editForm.setFieldValue(
                  "form_field_pass_change",
                  trimStart(e.target.value),
                );
              }}
              onBlur={(e) => {
                editForm.setFieldValue(
                  "form_field_pass_change",
                  trimInput(e.target.value),
                );
              }}
            />
          </Form.Item>
          <div style={{ color: "#666", fontSize: 12 }}>
            メールを変更した場合、確認メールのリンクをクリックすると反映されます。
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAccountsPage;
